import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, CurrencyField, FormGrid, Modal, NumberField, TextField, TextareaField, fmtBRL } from "@atlas/ui/web";
import { cobreQuitacaoRefin, conferirTrocoRefin, trocoSugeridoRefin } from "@atlas/domain";
import type { ContratoRefinanciavel, Refinanciamento } from "@atlas/sdk";
import { atlas } from "../lib/sdk";
import { mensagemDeErro } from "../lib/erro-validacao";
import { alternarOrigem, fmtTaxaFracao } from "../lib/refinanciamento";

/** "1,85" -> 0.0185. Vazio ou invalido -> null. */
export function taxaPercentualParaFracao(v: string): number | null {
  const n = Number(v.trim().replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n / 100 : null;
}

function Simulacao({ parcela, troco, diferenca, erro }: { parcela: number | null; troco: number | null; diferenca: number | null; erro?: string }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, padding: 12, borderRadius: 10, background: "var(--bg-elev-2)", fontSize: 13 }}>
      <Linha label="Parcela (margem)" v={parcela == null ? "—" : fmtBRL(parcela)} />
      <Linha label="Troco ao servidor" v={troco == null ? "—" : fmtBRL(troco)} />
      <Linha label="Diferença na parcela" v={diferenca == null ? "—" : `${diferenca > 0 ? "+" : diferenca < 0 ? "−" : ""} ${fmtBRL(Math.abs(diferenca))}`} />
      {erro ? <div style={{ gridColumn: "1 / -1", color: "var(--danger-500)" }}>{erro}</div> : null}
    </div>
  );
}

function Linha({ label, v }: { label: string; v: string }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: "var(--text-dim)", textTransform: "uppercase" }}>{label}</div>
      <div style={{ marginTop: 4, fontWeight: 600 }}>{v}</div>
    </div>
  );
}

function useTrocoSugerido(opts: {
  valorFinanciado: number | null;
  saldos: number[];
  permiteTroco: boolean;
}) {
  const sugerido = useMemo(() => {
    if (!opts.valorFinanciado) return null;
    if (!opts.permiteTroco) return 0;
    return trocoSugeridoRefin({ valorFinanciado: opts.valorFinanciado, saldosQuitacao: opts.saldos });
  }, [opts.valorFinanciado, opts.saldos, opts.permiteTroco]);
  const [troco, setTroco] = useState<number | null>(null);
  const [editado, setEditado] = useState(false);
  useEffect(() => {
    setEditado(false);
  }, [opts.saldos.join("|"), opts.permiteTroco]);
  useEffect(() => {
    if (!editado) setTroco(sugerido);
  }, [sugerido, editado]);
  return {
    troco,
    sugerido,
    setTroco: (v: number | null) => {
      setEditado(true);
      setTroco(v);
    },
  };
}

/** Banco oferece refinanciamento a partir de um contrato da carteira. */
export function OfertarRefinanciamentoModal({ adf, onClose, onFeito }: { adf: string; onClose: () => void; onFeito: (r: Refinanciamento) => void }) {
  const q = useQuery({
    queryKey: ["banco", "refinanciamentos", "elegiveis", adf],
    queryFn: () => atlas.refinanciamento.elegiveisNoVinculo(adf),
  });
  const itens = q.data?.itens ?? [];
  const [selecionadas, setSelecionadas] = useState<string[]>([adf]);
  const [valor, setValor] = useState<number | null>(null);
  const [parcela, setParcela] = useState<number | null>(null);
  const [prazo, setPrazo] = useState("");
  const [taxa, setTaxa] = useState("");

  const escolhidos: ContratoRefinanciavel[] = itens.filter((c) => selecionadas.includes(c.adf));
  const regras = escolhidos[0]?.regras;
  const prazoNum = Number(prazo);
  const taxaNum = taxaPercentualParaFracao(taxa);
  const parcelasOrigem = escolhidos.reduce((s, c) => s + c.valorParcela, 0);
  const saldos = escolhidos.map((c) => c.saldoDevedor);
  const { troco, sugerido, setTroco } = useTrocoSugerido({
    valorFinanciado: valor,
    saldos,
    permiteTroco: regras?.permiteTroco !== false,
  });

  const sim = useMemo(() => {
    if (!valor || !parcela || !(prazoNum >= 1) || !regras) return null;
    const quitacao = cobreQuitacaoRefin(valor, saldos);
    const t = conferirTrocoRefin(troco ?? 0, regras);
    return {
      parcela,
      troco: t.troco,
      erro: quitacao.erro ?? t.erro,
      diferenca: Math.round((parcela - parcelasOrigem) * 100) / 100,
    };
  }, [valor, parcela, prazoNum, regras, saldos, troco, parcelasOrigem]);

  const mut = useMutation({
    mutationFn: () =>
      atlas.refinanciamento.ofertar({
        origens: selecionadas,
        valorFinanciado: valor!,
        valorParcela: parcela!,
        prazo: prazoNum,
        taxaAm: taxaNum!,
        troco: troco ?? 0,
      }),
    onSuccess: onFeito,
  });
  const pronto = escolhidos.length > 0 && escolhidos.every((c) => c.elegivel) && sim != null && !sim.erro && taxaNum != null && troco != null;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      eyebrow="Refinanciamento"
      title="Ofertar refinanciamento"
      subtitle={`A partir do contrato ${adf}. O servidor aceita ou recusa no app.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Fechar</Button>
          <Button onClick={() => mut.mutate()} disabled={!pronto || mut.isPending}>
            {mut.isPending ? "Enviando…" : "Enviar oferta ao servidor"}
          </Button>
        </>
      }
    >
      {q.isLoading ? <p>Carregando contratos do servidor…</p> : null}
      {q.error ? <p style={{ color: "var(--danger-500)" }}>{mensagemDeErro(q.error, "Não foi possível carregar os contratos.")}</p> : null}
      <div style={{ display: "grid", gap: 8, marginBottom: 14 }}>
        {itens.map((c) => (
          <label key={c.adf} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 13, opacity: c.elegivel ? 1 : 0.6 }}>
            <input
              type="checkbox"
              checked={selecionadas.includes(c.adf)}
              disabled={!c.elegivel}
              onChange={() => setSelecionadas((s) => alternarOrigem(s, c, itens))}
            />
            <span style={{ fontFamily: "var(--font-mono)" }}>{c.adf}</span>
            <span>parcela {fmtBRL(c.valorParcela)} · saldo {fmtBRL(c.saldoDevedor)} · {c.parcelasPagas}/{c.totalParcelas} pagas</span>
            {c.motivo ? <span style={{ color: "var(--gold-500)" }}>— {c.motivo}</span> : null}
          </label>
        ))}
      </div>
      <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 0 }}>
        Parcela e troco são do seu cálculo. A averbadora não simula juros (Price/PMT).
      </p>
      <FormGrid>
        <CurrencyField label="Valor financiado" value={valor} onValueChange={setValor} required />
        <CurrencyField
          label="Parcela (margem)"
          value={parcela}
          onValueChange={setParcela}
          required
          hint="O valor que entra na folha. Calcule no seu sistema."
        />
        <NumberField label="Prazo (parcelas)" value={prazo} onChange={(e) => setPrazo(e.target.value)} min={1} max={120} required />
        <TextField label="Taxa (% a.m.)" value={taxa} onChange={(e) => setTaxa(e.target.value)} inputMode="decimal" placeholder="1,85" required />
        <CurrencyField
          label="Troco"
          value={troco}
          onValueChange={setTroco}
          required
          hint={
            sugerido == null
              ? "Sugestão: valor financiado − saldo."
              : `Sugerido ${fmtBRL(sugerido)} (valor financiado − saldo). Pode ajustar.`
          }
        />
      </FormGrid>
      <div style={{ marginTop: 12 }}>
        <Simulacao parcela={sim?.parcela ?? null} troco={sim?.troco ?? null} diferenca={sim?.diferenca ?? null} erro={sim?.erro} />
      </div>
      {mut.error ? <p role="alert" style={{ color: "var(--danger-500)" }}>{mensagemDeErro(mut.error, "Não foi possível enviar a oferta.")}</p> : null}
    </Modal>
  );
}

/** Banco aprova (com as condicoes dele) ou recusa o pedido do servidor. */
export function ResponderRefinanciamentoModal({ refin, onClose, onFeito }: { refin: Refinanciamento; onClose: () => void; onFeito: (r: Refinanciamento) => void }) {
  const [acao, setAcao] = useState<"aprovar" | "recusar">("aprovar");
  const pedido = refin.pedido;
  const [valor, setValor] = useState<number | null>(pedido?.valor ?? null);
  const [parcela, setParcela] = useState<number | null>(pedido?.parcela ?? null);
  const [prazo, setPrazo] = useState(pedido?.prazo != null ? String(pedido.prazo) : "");
  const [taxa, setTaxa] = useState("");
  const [motivo, setMotivo] = useState("");

  const prazoNum = Number(prazo);
  const taxaNum = taxaPercentualParaFracao(taxa);
  const saldo = refin.origens.reduce((s, o) => s + (o.saldoQuitacao ?? o.saldoDevedor), 0);
  const saldos = refin.origens.map((o) => o.saldoQuitacao ?? o.saldoDevedor);
  const { troco, sugerido, setTroco } = useTrocoSugerido({
    valorFinanciado: valor,
    saldos,
    permiteTroco: true,
  });

  const sim = useMemo(() => {
    if (!valor || !parcela || !(prazoNum >= 1)) return null;
    const quitacao = cobreQuitacaoRefin(valor, saldos);
    const t = conferirTrocoRefin(troco ?? 0, { permiteTroco: true, trocoMinimo: 0 });
    return {
      valorFinanciado: valor,
      parcela,
      troco: t.troco,
      erro: quitacao.erro ?? t.erro,
      diferenca: Math.round((parcela - refin.parcelasOrigem) * 100) / 100,
    };
  }, [valor, parcela, prazoNum, saldos, troco, refin.parcelasOrigem]);

  const igualAoPedido =
    acao === "aprovar" &&
    pedido?.parcela != null &&
    pedido?.prazo != null &&
    parcela === pedido.parcela &&
    prazoNum === pedido.prazo &&
    (pedido.valor == null || pedido.valor === valor);

  const mut = useMutation({
    mutationFn: () =>
      acao === "recusar"
        ? atlas.refinanciamento.responder(refin.id, { acao: "recusar", motivo: motivo.trim() || undefined })
        : atlas.refinanciamento.responder(refin.id, {
            acao: "aprovar",
            taxaAm: taxaNum!,
            prazo: prazoNum,
            valorFinanciado: valor ?? undefined,
            valorParcela: parcela ?? undefined,
            troco: troco ?? 0,
          }),
    onSuccess: onFeito,
  });
  const pronto = acao === "recusar" ? true : sim != null && !sim.erro && taxaNum != null && troco != null;

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      eyebrow="Refinanciamento"
      title={`Responder pedido ${refin.id}`}
      subtitle={`${refin.servidorNome} · matrícula ${refin.matricula} · ${refin.origens.map((o) => o.adf).join(", ")}`}
      danger={acao === "recusar"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Fechar</Button>
          <Button onClick={() => mut.mutate()} disabled={!pronto || mut.isPending}>
            {mut.isPending ? "Enviando…" : acao === "recusar" ? "Recusar pedido" : igualAoPedido ? "Aprovar como pedido" : "Enviar contraproposta"}
          </Button>
        </>
      }
    >
      <div style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 12 }}>
        Pedido: {pedido?.parcela != null ? `parcela ${fmtBRL(pedido.parcela)}` : ""}
        {pedido?.valor != null ? `valor ${fmtBRL(pedido.valor)}` : ""}
        {pedido?.prazo != null ? ` em ${pedido.prazo}x` : ""} · parcelas atuais {fmtBRL(refin.parcelasOrigem)} · saldo {fmtBRL(saldo)}
      </div>
      <div role="radiogroup" aria-label="Decisão" style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <Button variant={acao === "aprovar" ? undefined : "ghost"} onClick={() => setAcao("aprovar")}>Aprovar</Button>
        <Button variant={acao === "recusar" ? undefined : "ghost"} onClick={() => setAcao("recusar")}>Recusar</Button>
      </div>
      {acao === "recusar" ? (
        <TextareaField label="Motivo (o servidor vê)" value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} />
      ) : (
        <>
          <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 0 }}>
            Parcela e troco são do seu cálculo. A averbadora não simula juros (Price/PMT).
          </p>
          <FormGrid>
            <CurrencyField label="Valor financiado" value={valor} onValueChange={setValor} required />
            <CurrencyField
              label="Parcela (margem)"
              value={parcela}
              onValueChange={setParcela}
              required
              hint={pedido?.parcela != null ? `Pedida: ${fmtBRL(pedido.parcela)}` : "O valor que entra na folha."}
            />
            <NumberField label="Prazo (parcelas)" value={prazo} onChange={(e) => setPrazo(e.target.value)} min={1} max={120} required />
            <TextField label="Taxa (% a.m.)" value={taxa} onChange={(e) => setTaxa(e.target.value)} inputMode="decimal" placeholder="1,85" required />
            <CurrencyField
              label="Troco"
              value={troco}
              onValueChange={setTroco}
              required
              hint={
                sugerido == null
                  ? "Sugestão: valor financiado − saldo."
                  : `Sugerido ${fmtBRL(sugerido)} (valor financiado − saldo). Pode ajustar.`
              }
            />
          </FormGrid>
          <div style={{ marginTop: 12 }}>
            <Simulacao parcela={sim?.parcela ?? null} troco={sim?.troco ?? null} diferenca={sim?.diferenca ?? null} erro={sim?.erro} />
          </div>
          <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 0 }}>
            {igualAoPedido
              ? "Parcela e prazo iguais ao pedido: o servidor já autorizou com senha, e o contrato sucessor nasce aprovado."
              : "Condições diferentes do pedido viram contraproposta: o servidor precisa aceitar no app."}
            {sim && taxaNum ? ` Taxa informada: ${fmtTaxaFracao(taxaNum)}.` : ""}
          </p>
        </>
      )}
      {mut.error ? <p role="alert" style={{ color: "var(--danger-500)" }}>{mensagemDeErro(mut.error, "Não foi possível responder.")}</p> : null}
    </Modal>
  );
}

export function CancelarRefinanciamentoModal({ refin, onClose, onFeito }: { refin: Refinanciamento; onClose: () => void; onFeito: (r: Refinanciamento) => void }) {
  const [motivo, setMotivo] = useState("");
  const mut = useMutation({
    mutationFn: () => atlas.refinanciamento.cancelarPeloBanco(refin.id, motivo.trim() || undefined),
    onSuccess: onFeito,
  });
  return (
    <Modal
      open
      onClose={onClose}
      danger
      title={`Cancelar refinanciamento ${refin.id}`}
      subtitle="Os contratos de origem voltam a ficar livres para outro pedido."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Voltar</Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending}>{mut.isPending ? "Cancelando…" : "Cancelar refinanciamento"}</Button>
        </>
      }
    >
      <TextareaField label="Motivo (o servidor vê)" value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={3} />
      {mut.error ? <p role="alert" style={{ color: "var(--danger-500)" }}>{mensagemDeErro(mut.error, "Não foi possível cancelar.")}</p> : null}
    </Modal>
  );
}
